/**
 * Ficha técnica imprimible de una propiedad (workflow del analista:
 * llevar la oportunidad a una mesa de inversión / compartirla en PDF).
 *
 * Genera un documento HTML autocontenido (estilos inline, tema claro
 * print-friendly) y lo abre en una pestaña nueva con el diálogo de
 * impresión listo — "Guardar como PDF" del browser produce la ficha
 * sin dependencias de librerías PDF en el bundle.
 */

import type { PropertyDetail } from './types';
import { ESTADO_NAMES, humanize } from './filters';

function esc(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Solo URLs http(s) van a un href — los sourceUrl vienen de scraping de
 * portales externos y un `javascript:`/`data:` colado ejecutaría script
 * al click en la ficha. Devuelve null si el esquema no es seguro.
 */
function safeUrl(v: string | null | undefined): string | null {
  if (!v) return null;
  const u = v.trim();
  return /^https?:\/\//i.test(u) ? u : null;
}

function money(n: number | null | undefined, currency: string): string {
  if (n === null || n === undefined || n <= 1) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency || 'MXN',
    maximumFractionDigits: 0,
  }).format(Number(n));
}

function date(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' });
}

function boolTxt(v: boolean | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v ? 'Sí' : 'No';
}

/** pg devuelve NUMERIC como string — aceptamos ambos. */
function num(v: number | string | null | undefined, suffix = ''): string {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toLocaleString('es-MX')}${suffix}`;
}

/** Fila <tr> etiqueta/valor; el valor ya debe venir escapado si es texto libre. */
function row(label: string, valueHtml: string): string {
  return `<tr><th>${esc(label)}</th><td>${valueHtml || '—'}</td></tr>`;
}

export interface FichaOptions {
  /** Coordenadas ya parseadas del GeoJSON (las parsea el drawer). */
  coords?: { lat: number; lng: number } | null;
}

export function buildFichaHtml(d: PropertyDetail, opts: FichaOptions = {}): string {
  const estado = d.cve_ent ? ESTADO_NAMES[d.cve_ent] ?? d.cve_ent : null;
  const direccion = d.address_normalized ?? d.address_raw ?? 'Sin dirección';
  const photo = (d.photo_urls ?? [])[0];
  const coords = opts.coords ?? null;
  const deepLink = `${window.location.origin}/?property=${d.id}`;
  const score = d.opportunity_score !== null && d.opportunity_score !== undefined
    ? Math.round(d.opportunity_score)
    : null;

  const factores: Array<[string, number | null | undefined]> = [
    ['Precio vs zona (28%)', d.opp_factor_price],
    ['Seguridad (23%)', d.opp_factor_safety],
    ['Completitud (18%)', d.opp_factor_completeness],
    ['Legal (14%)', d.opp_factor_legal],
    ['Liquidez (7%)', d.opp_factor_liquidity],
    ['Riesgo físico (10%)', d.opp_factor_physical],
  ];

  const priceHistory = (d.price_history ?? []).filter((h) => h.previousPrice !== null);

  const badges: string[] = [];
  badges.push(`<span class="badge">${esc(humanize('channel', d.legal_channel))}</span>`);
  badges.push(`<span class="badge badge-accent">${esc(humanize('status', d.acquisition_status))}</span>`);
  if (score !== null) badges.push(`<span class="badge badge-score">Opportunity ${score}/100</span>`);
  if (d.opp_has_litigio === true) badges.push('<span class="badge badge-warn">⚠ Litigio pendiente</span>');
  if (d.opp_is_libre_gravamen === true) badges.push('<span class="badge badge-good">✓ Libre de gravamen</span>');
  if (d.opp_discount_vs_avaluo_pct !== null && d.opp_discount_vs_avaluo_pct !== undefined
      && d.opp_discount_vs_avaluo_pct >= 25) {
    badges.push(`<span class="badge badge-good">★ ${d.opp_discount_vs_avaluo_pct.toFixed(0)}% bajo avalúo</span>`);
  }

  const sources = (d.sources ?? []).map((s) => {
    const url = safeUrl(s.sourceUrl);
    return `<tr><th>${esc(s.sourceType)}</th><td>${
      url ? `<a href="${esc(url)}">${esc(url)}</a>` : esc(s.sourceId)
    }</td></tr>`;
  }).join('');

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Ficha — ${esc(direccion)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font: 13px/1.45 -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #1a2233; background: #fff; padding: 32px 40px; max-width: 820px; margin: 0 auto;
  }
  header.brand {
    display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 2px solid #1a2233; padding-bottom: 10px; margin-bottom: 18px;
  }
  .brand h1 { font-size: 16px; letter-spacing: .4px; }
  .brand h1 small { font-weight: 400; color: #5a6478; }
  .brand .gen { font-size: 11px; color: #5a6478; }
  .hero { display: flex; gap: 18px; margin-bottom: 18px; }
  .hero img { width: 280px; height: 190px; object-fit: cover; border-radius: 6px; border: 1px solid #d8dce5; }
  .hero-info { flex: 1; }
  .price { font-size: 26px; font-weight: 700; }
  .price .disc { font-size: 14px; color: #b42318; margin-left: 8px; font-weight: 600; }
  .addr { font-size: 14px; margin: 6px 0 2px; }
  .loc { color: #5a6478; margin-bottom: 10px; }
  .badges { display: flex; flex-wrap: wrap; gap: 6px; }
  .badge { font-size: 11px; border: 1px solid #c5ccd9; border-radius: 99px; padding: 2px 9px; color: #38415a; }
  .badge-accent { background: #eef2fb; border-color: #b9c7ec; }
  .badge-score { background: #1a2233; color: #fff; border-color: #1a2233; }
  .badge-warn { background: #fef3e6; border-color: #f0c389; color: #93540b; }
  .badge-good { background: #e9f7ef; border-color: #9ed8b5; color: #14693a; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 28px; }
  section { break-inside: avoid; margin-bottom: 16px; }
  h2 {
    font-size: 12px; text-transform: uppercase; letter-spacing: .8px; color: #5a6478;
    border-bottom: 1px solid #d8dce5; padding-bottom: 4px; margin-bottom: 6px;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { text-align: left; padding: 3px 0; vertical-align: top; font-size: 12.5px; }
  th { color: #5a6478; font-weight: 500; width: 46%; padding-right: 10px; }
  td { color: #1a2233; }
  td a { color: #2456c4; word-break: break-all; text-decoration: none; }
  .factor-bar { display: inline-block; height: 7px; background: #2456c4; border-radius: 4px; vertical-align: middle; margin-right: 6px; }
  .factor-track { display: inline-block; width: 110px; height: 7px; background: #e7eaf1; border-radius: 4px; vertical-align: middle; margin-right: 6px; }
  .factor-track .factor-bar { display: block; margin: 0; }
  footer {
    margin-top: 22px; padding-top: 10px; border-top: 1px solid #d8dce5;
    font-size: 10.5px; color: #5a6478;
  }
  footer a { color: #2456c4; }
  .print-hint { position: fixed; top: 10px; right: 12px; }
  .print-hint button {
    font: 600 12px/1 inherit; padding: 8px 14px; border-radius: 6px; border: 0;
    background: #1a2233; color: #fff; cursor: pointer;
  }
  @media print { .print-hint { display: none; } body { padding: 0; } }
  @page { margin: 16mm; }
</style>
</head>
<body>
  <div class="print-hint"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>

  <header class="brand">
    <h1>TerraKairos <small>— Inteligencia Inmobiliaria Distressed</small></h1>
    <span class="gen">Ficha generada el ${esc(new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }))}</span>
  </header>

  <div class="hero">
    ${photo ? `<img src="${esc(photo)}" alt="Foto de la propiedad" />` : ''}
    <div class="hero-info">
      <div class="price">${esc(money(d.asking_price, d.currency))}${
        d.discount_pct !== null && d.discount_pct > 0
          ? `<span class="disc">-${Number(d.discount_pct).toFixed(0)}%</span>` : ''
      }</div>
      <div class="addr">${esc(direccion)}</div>
      <div class="loc">${esc([humanize('propertyType', d.property_type), d.colonia, d.municipio, estado].filter(Boolean).join(' · '))}</div>
      <div class="badges">${badges.join('')}</div>
    </div>
  </div>

  <div class="grid">
    <section>
      <h2>Localización</h2>
      <table>
        ${row('Estado', esc(estado))}
        ${row('Municipio', esc(d.municipio))}
        ${row('Colonia', esc(d.colonia))}
        ${row('Código postal', esc(d.codigo_postal))}
        ${row('Coordenadas', coords ? esc(`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`) : '—')}
        ${coords ? row('Google Maps', `<a href="https://maps.google.com/?q=${coords.lat},${coords.lng}">maps.google.com/?q=${coords.lat.toFixed(5)},${coords.lng.toFixed(5)}</a>`) : ''}
      </table>
    </section>

    <section>
      <h2>Económico</h2>
      <table>
        ${row('Precio listado', esc(money(d.asking_price, d.currency)))}
        ${row('Avalúo', esc(money(d.avaluo_price, d.currency)))}
        ${row('Descuento vs avalúo', d.opp_discount_vs_avaluo_pct !== null && d.opp_discount_vs_avaluo_pct !== undefined && d.opp_discount_vs_avaluo_pct > 0 ? esc(`${d.opp_discount_vs_avaluo_pct.toFixed(1)}%`) : '—')}
        ${row('$/m²', d.opp_price_m2 !== null && d.opp_price_m2 !== undefined ? esc(num(Math.round(d.opp_price_m2))) : '—')}
        ${row('$/m² promedio zona', d.opp_zone_avg_price_m2 !== null && d.opp_zone_avg_price_m2 !== undefined ? esc(`${num(Math.round(d.opp_zone_avg_price_m2))} (${d.opp_zone_n_comparables ?? '?'} comparables)`) : '—')}
        ${row('Adeudo predial', esc(money(d.predial_balance, d.currency)))}
        ${row('Postura mínima', esc(money(d.minimum_bid, d.currency)))}
      </table>
    </section>

    <section>
      <h2>Características</h2>
      <table>
        ${row('Tipo', esc(humanize('propertyType', d.property_type)))}
        ${row('Construcción', d.surface_built_m2 !== null ? esc(num(d.surface_built_m2, ' m²')) : '—')}
        ${row('Terreno', d.surface_terrain_m2 !== null ? esc(num(d.surface_terrain_m2, ' m²')) : '—')}
        ${row('Recámaras', esc(d.bedrooms ?? '—'))}
        ${row('Baños', esc(d.bathrooms ?? '—'))}
        ${row('Estacionamiento', esc(d.parking ?? '—'))}
        ${row('Año de construcción', esc(d.year_built ?? '—'))}
      </table>
    </section>

    <section>
      <h2>Estado legal</h2>
      <table>
        ${row('Canal', esc(humanize('channel', d.legal_channel)))}
        ${row('Estatus', esc(humanize('status', d.acquisition_status)))}
        ${row('Hipoteca vigente', esc(boolTxt(d.hipoteca_vigente)))}
        ${row('Libre de gravamen', esc(boolTxt(d.libre_de_gravamen)))}
        ${row('Embargo activo', esc(boolTxt(d.embargo_activo)))}
        ${row('Litigio pendiente', esc(boolTxt(d.litigio_pendiente)))}
        ${row('Ejido', esc(boolTxt(d.ejido_flag)))}
        ${row('Zona de riesgo', esc(boolTxt(d.zona_riesgo_flag)))}
      </table>
    </section>

    ${score !== null ? `
    <section>
      <h2>Opportunity Score — ${score}/100</h2>
      <table>
        ${factores.map(([label, v]) => row(
          label,
          v !== null && v !== undefined
            ? `<span class="factor-track"><span class="factor-bar" style="width:${Math.max(0, Math.min(100, v))}%"></span></span>${Math.round(v)}`
            : '—',
        )).join('')}
      </table>
    </section>` : ''}

    ${(d.cenapred_seismic_zone || d.cenapred_flood_risk) ? `
    <section>
      <h2>Riesgos físicos (CENAPRED)</h2>
      <table>
        ${row('Zona sísmica (NTC)', d.cenapred_seismic_zone ? esc(`Zona ${d.cenapred_seismic_zone}`) : '—')}
        ${row('Riesgo de inundación', esc(d.cenapred_flood_risk ?? '—'))}
      </table>
    </section>` : ''}

    ${priceHistory.length > 0 ? `
    <section>
      <h2>Historial de precio</h2>
      <table>
        ${priceHistory.map((h) => {
          const prev = Number(h.previousPrice);
          const cur = Number(h.askingPrice);
          const pct = prev > 0 ? ((cur - prev) / prev) * 100 : null;
          return row(
            date(h.changedAt),
            `${esc(money(cur, d.currency))}${pct !== null ? ` (${pct < 0 ? '↓' : '↑'} ${Math.abs(pct).toFixed(1)}%)` : ''}`,
          );
        }).join('')}
      </table>
    </section>` : ''}

    <section>
      <h2>Fuentes y trazabilidad</h2>
      <table>
        ${sources}
        ${row('Detectado', esc(date(d.first_seen_at)))}
        ${row('Actualizado', esc(date(d.last_updated_at)))}
        ${row('ID TerraKairos', `<a href="${esc(deepLink)}">${esc(d.id)}</a>`)}
      </table>
    </section>
  </div>

  <footer>
    Generado por TerraKairos a partir de fuentes públicas (portales bancarios, INDEP, CENAPRED, SESNSP, SHF).
    Los datos reflejan el listing original y pueden cambiar sin aviso — verificar con la institución antes de ofertar.
    Ficha viva: <a href="${esc(deepLink)}">${esc(deepLink)}</a>
  </footer>
</body>
</html>`;
}

/**
 * Abre la ficha en una pestaña nueva lista para imprimir / guardar PDF.
 * Vía Blob URL (no document.write, que algunos browsers embebidos
 * bloquean). El URL se revoca tras 60s — de sobra para que cargue.
 */
export function openFicha(d: PropertyDetail, opts: FichaOptions = {}): void {
  const blob = new Blob([buildFichaHtml(d, opts)], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
